import React from 'react'
import { Controller, Control, FieldErrors } from 'react-hook-form'
import BaseQuestion from '../../base-question'
import SurveyDateInput from './date-input'
import { parseDate, stringifyDate } from 'utilities/dates'

interface DateQuestionProps {
  question: {
    id: number | string
    text: string
    isRequired?: boolean
    type: string
    placeholder?: string
  }
  // RHF mode
  control?: Control<any>
  errors?: FieldErrors<any>
  // Zustand mode
  value?: string
  error?: string
  onChange?: (value: string | undefined) => void
  onBlur?: () => void
}

const DateQuestion: React.FC<DateQuestionProps> = ({
  question,
  control,
  errors,
  value,
  error,
  onChange,
  onBlur,
}) => {
  const isRHFMode = !!control && !!errors

  // Helper to parse stored date value (supports timestamp, YYYY-MM-DD, and MM/DD/YYYY formats)
  const parseDateValue = (
    value: string | number | Date | undefined
  ): Date | null => {
    if (!value) return null

    // If already a Date, return it
    if (value instanceof Date) return value

    const valueStr = String(value)

    // Try parsing YYYY-MM-DD format first (ISO format for storage)
    const yyyymmdd = valueStr.match(/^(\d{4})-(\d{2})-(\d{2})$/)
    if (yyyymmdd) {
      const [, year, month, day] = yyyymmdd
      const parsed = new Date(
        parseInt(year),
        parseInt(month) - 1,
        parseInt(day)
      )
      return isNaN(parsed.getTime()) ? null : parsed
    }

    // Try parsing MM/DD/YYYY format (for backward compatibility)
    const mmddyyyy = valueStr.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
    if (mmddyyyy) {
      const [, month, day, year] = mmddyyyy
      const parsed = new Date(
        parseInt(year),
        parseInt(month) - 1,
        parseInt(day)
      )
      return isNaN(parsed.getTime()) ? null : parsed
    }

    // Use parseDate utility for timestamps (handles UTC offset adjustments)
    return parseDate(valueStr)
  }

  // Helper to format Date as YYYY-MM-DD string (ISO format for storage)
  const formatDateValue = (date: Date | null): string | undefined => {
    return stringifyDate(date)
  }

  const questionId =
    typeof question.id === 'string' ? parseInt(question.id, 10) : question.id

  if (isRHFMode) {
    // RHF mode - use Controller
    return (
      <BaseQuestion
        question={{
          ...question,
          id: questionId,
        }}
        control={control}
        errors={errors}>
        <Controller
          name={`question_${question.id}`}
          control={control}
          rules={{
            required: question.isRequired ? 'This field is required' : false,
          }}
          render={({ field }) => {
            const dateValue = parseDateValue(field.value)

            return (
              <SurveyDateInput
                value={dateValue}
                onChange={(date: Date | null) => {
                  // Store as YYYY-MM-DD string format when valid date is entered
                  field.onChange(formatDateValue(date))
                }}
                onBlur={(date: Date | null) => {
                  // Final validation and storage on blur
                  field.onChange(formatDateValue(date))
                }}
                placeholder={question.placeholder || ''}
                questionId={questionId}
              />
            )
          }}
        />
      </BaseQuestion>
    )
  } else {
    // Zustand mode - use direct props
    const dateValue = parseDateValue(value)
    return (
      <BaseQuestion
        question={{
          ...question,
          id: questionId,
        }}
        error={error}>
        <SurveyDateInput
          value={dateValue}
          onChange={(date: Date | null) => {
            // Store as YYYY-MM-DD string format when valid date is entered
            onChange?.(formatDateValue(date))
          }}
          onBlur={(date: Date | null) => {
            // Final validation and storage on blur
            onChange?.(formatDateValue(date))
            // Call parent onBlur to mark field as touched
            onBlur?.()
          }}
          placeholder={question.placeholder || ''}
          questionId={questionId}
        />
      </BaseQuestion>
    )
  }
}

export default DateQuestion
